import { isFuture } from "date-fns";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { FC } from "react";
import { generatePath, useSearchParams } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import { Badge, HStack, Stack, Text, VStack } from "shared/components/Spor";
import { ReserveAlternativeTransport } from "shared/types/alternativeTransport";
import {
  getPrintableFromVehicleType,
  toSentenceCase,
} from "shared/utils/alternativeTransport";
import { BACKUPBUS, RESERVEBUS } from "shared/utils/routes";
import { useTheme } from "styled-components";

type Props = {
  reserveAlternativeTransport: ReserveAlternativeTransport;
  elementSelected: boolean;
  isReserveVehicle: boolean;
  isLastElement?: boolean;
};

export const ReserveOrBackupAlternativeTransportElement: FC<Props> = ({
  reserveAlternativeTransport,
  elementSelected,
  isReserveVehicle,
  isLastElement = false,
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const {
    workShiftId,
    shiftStartTime,
    shiftEndTime,
    driverPhoneNumber,
    isAvailable,
    baseStopName,
    type,
    operatorName,
    externalSystem,
  } = reserveAlternativeTransport;

  const hasPhoneNumber = !!driverPhoneNumber && driverPhoneNumber.length > 0;

  const isFutureShift = shiftEndTime && isFuture(shiftEndTime);

  const isPast = !isFutureShift;

  const textColorMain = isPast
    ? theme.colorTextTertiary
    : theme.colorTextSecondary;

  /*
    This is a workaround. If we send in the plus to Puzzel, it is stripped away
    before the 18740 prefix is added. This works fine if you're an admin, but
    not for normal DROPS users. But if we replace the + with 00, it is not stripped,
    and with the 00 it works just fine. Don't ask me why.
   
    These formats work for everyone:
    18740 0047 12345678 <- Result from what we send in with the workaround
    18740 12345678
    0047 12345678
    12345678

    This format only works for admins:
    18740 47 12345678 <- Result from what we send in without the workaround
  */
  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;

  const path = isReserveVehicle ? RESERVEBUS : BACKUPBUS;
  const vehicleTitle = isReserveVehicle
    ? `Reserve ${workShiftId ?? ""}`
    : `Beredskap (${operatorName ?? ""})`;

  return (
    <ElementWrapper
      selected={elementSelected}
      backgroundColor={theme.colorBackgroundSecondary}
      color={textColorMain}
      isHover
      to={`${generatePath(path, {
        vehicleUuid: reserveAlternativeTransport.id,
      })}?${searchParams.toString()}`}
      linkState={{ vehicleTitle }}
      hideDashedLine={isLastElement}
    >
      <Stack
        gap="0.5"
        flex={1}
        _focusVisible={{
          borderRadius: "6px",
        }}
      >
        <HStack height="25px" gap="6px">
          <Text fontWeight="bold" color={textColorMain} variant="xs">
            {vehicleTitle}
          </Text>
          {externalSystem && (
            <Text variant="sm" color={theme.colorTextSecondary}>
              {toSentenceCase(externalSystem)}
            </Text>
          )}
        </HStack>

        {`${baseStopName}, ${getPrintableFromVehicleType(type) ?? ""}`}
        <TimeInterval
          startTime={shiftStartTime}
          endTime={shiftEndTime}
          disabled={isPast}
        />
      </Stack>

      <VStack
        alignItems="flex-end"
        justifyContent="space-between"
        alignSelf="stretch"
      >
        <HStack>
          {!isAvailable && <Badge colorPalette="blue">I bruk</Badge>}
        </HStack>

        {hasPhoneNumber && (
          <CallButton phoneNumber={phoneNumberWithDigitsOnly!} size="xs" />
        )}
      </VStack>
    </ElementWrapper>
  );
};
